import type { LscAutomationActivationStatus } from './types';

export const COMMAND_CENTER_SETUP_TITLE = 'League setup';
export const COMMAND_CENTER_SETUP_COMPLETE_COPY =
  'Your league setup checks are complete. You can still revisit the wizard anytime.';
export const COMMAND_CENTER_SETUP_INCOMPLETE_COPY =
  'Finish setup to unlock automation activation and full league operations.';
export const COMMAND_CENTER_RESUME_SETUP_LABEL = 'Resume setup';

export const COMMAND_CENTER_ACTIVATION_TITLE = 'Automation activation';
export const COMMAND_CENTER_BUNDLE_TITLE = 'Default automation bundle';
export const COMMAND_CENTER_AUTOPUBLISH_STAGE2_COPY =
  'Archive auto-publish is off (Stage 2). Commissioner review is still required before publish.';

export const ARCHIVE_AUTOPUBLISH_OFF_COPY =
  'Archive autopublish is off. Commissioner review remains required before publish.';

export const ARCHIVE_AUTOPUBLISH_DRY_RUN_COPY =
  'Archive autopublish dry run can preview what would publish. Nothing is published automatically.';

export const ARCHIVE_AUTOPUBLISH_CONTROLLED_LOCKED_COPY =
  'Controlled publishing is locked until post-launch Stage 2 enablement.';

export const ARCHIVE_AUTOPUBLISH_ELIGIBLE_FOR_DRY_RUN_COPY =
  'This archive candidate would qualify for a dry-run preview. Nothing is published automatically.';

export const ARCHIVE_AUTOPUBLISH_BLOCKED_COPY =
  'This archive candidate is not eligible for autopublish. Review remains required.';

export const ARCHIVE_AUTOPUBLISH_DRY_RUN_PREVIEW_OFF_COPY =
  'Archive autopublish dry run is off. Commissioner review remains required before publish.';

export const ARCHIVE_AUTOPUBLISH_DRY_RUN_PREVIEW_AVAILABLE_COPY =
  'Archive autopublish dry run can preview what would happen. No archive will publish automatically yet.';

export const ARCHIVE_AUTOPUBLISH_WOULD_PUBLISH_IN_DRY_RUN_COPY =
  'This archive would be eligible for autopublish in dry run. No archive will be published yet. Manual review remains available.';

export const ARCHIVE_AUTOPUBLISH_WOULD_ROUTE_TO_REVIEW_COPY =
  'This archive would be sent to review. No archive will publish automatically yet.';

export const ARCHIVE_AUTOPUBLISH_NO_PUBLISH_WILL_HAPPEN_COPY =
  'No archive will publish automatically yet.';

export const ARCHIVE_AUTOPUBLISH_MANUAL_REVIEW_FALLBACK_COPY =
  'Manual review remains the fallback.';

export const ARCHIVE_AUTOPUBLISH_KILL_SWITCH_GLOBAL_PAUSED_COPY =
  'Archive autopublish is temporarily paused. No archive will publish automatically yet. Manual review remains available.';

export const ARCHIVE_AUTOPUBLISH_KILL_SWITCH_LEAGUE_PAUSED_COPY =
  'Archive autopublish is paused for this league. No archive will publish automatically yet. Manual review remains available.';

export const ARCHIVE_AUTOPUBLISH_KILL_SWITCH_CANDIDATE_HOLD_COPY =
  'This archive is on manual hold. No archive will publish automatically yet. Manual review remains available.';

export function archiveAutopublishKillSwitchCustomerCopy(
  summary?: {
    global_disabled?: boolean;
    league_disabled?: boolean;
    candidate_hold_active?: boolean;
  } | null
): string {
  if (summary?.global_disabled) {
    return ARCHIVE_AUTOPUBLISH_KILL_SWITCH_GLOBAL_PAUSED_COPY;
  }
  if (summary?.league_disabled) {
    return ARCHIVE_AUTOPUBLISH_KILL_SWITCH_LEAGUE_PAUSED_COPY;
  }
  if (summary?.candidate_hold_active) {
    return ARCHIVE_AUTOPUBLISH_KILL_SWITCH_CANDIDATE_HOLD_COPY;
  }
  return ARCHIVE_AUTOPUBLISH_NO_PUBLISH_WILL_HAPPEN_COPY;
}

export function archiveAutopublishSettingsCustomerCopy(mode?: string | null): string {
  switch ((mode || '').trim().toLowerCase()) {
    case 'dry_run':
      return ARCHIVE_AUTOPUBLISH_DRY_RUN_COPY;
    case 'controlled_apply':
      return ARCHIVE_AUTOPUBLISH_CONTROLLED_LOCKED_COPY;
    case 'off':
    default:
      return ARCHIVE_AUTOPUBLISH_OFF_COPY;
  }
}

export function archiveAutopublishEligibilityCustomerCopy(mode?: string | null): string {
  switch ((mode || '').trim().toLowerCase()) {
    case 'eligible_for_dry_run':
      return ARCHIVE_AUTOPUBLISH_ELIGIBLE_FOR_DRY_RUN_COPY;
    case 'dry_run':
      return ARCHIVE_AUTOPUBLISH_DRY_RUN_COPY;
    case 'controlled_apply_locked':
    case 'controlled_apply':
      return ARCHIVE_AUTOPUBLISH_CONTROLLED_LOCKED_COPY;
    case 'blocked':
      return ARCHIVE_AUTOPUBLISH_BLOCKED_COPY;
    case 'off':
    default:
      return ARCHIVE_AUTOPUBLISH_OFF_COPY;
  }
}

export function archiveAutopublishDryRunPreviewCustomerCopy(mode?: string | null): string {
  switch ((mode || '').trim().toLowerCase()) {
    case 'eligible_for_dry_run':
      return ARCHIVE_AUTOPUBLISH_WOULD_PUBLISH_IN_DRY_RUN_COPY;
    case 'review_required':
    case 'blocked':
    case 'paused':
      return ARCHIVE_AUTOPUBLISH_WOULD_ROUTE_TO_REVIEW_COPY;
    case 'dry_run':
      return ARCHIVE_AUTOPUBLISH_DRY_RUN_PREVIEW_AVAILABLE_COPY;
    case 'controlled_apply_locked':
      return ARCHIVE_AUTOPUBLISH_CONTROLLED_LOCKED_COPY;
    case 'off':
    default:
      return ARCHIVE_AUTOPUBLISH_DRY_RUN_PREVIEW_OFF_COPY;
  }
}

export function archiveAutopublishCommandCenterSummary(
  settingsMode?: string | null,
  killSwitch?: { kill_switch_active?: boolean; global_disabled?: boolean; league_disabled?: boolean } | null
): string {
  if (killSwitch?.global_disabled) {
    return 'Archive autopublish is temporarily paused. Safety controls are active.';
  }
  if (killSwitch?.league_disabled || killSwitch?.kill_switch_active) {
    return 'Archive autopublish is paused for this league. Safety controls are active.';
  }
  switch ((settingsMode || 'off').trim().toLowerCase()) {
    case 'dry_run':
      return 'Dry run is ready. No archive will publish automatically yet.';
    case 'controlled_apply':
    case 'controlled_apply_locked':
      return 'Controlled publishing is locked. Safety controls are active.';
    case 'off':
    default:
      return 'Archive autopublish is off. Commissioner review remains required before publish.';
  }
}

export const COMMAND_CENTER_REVIEW_TITLE = 'Review work';
export const COMMAND_CENTER_REVIEW_INTRO_COPY =
  'Games that need a commissioner check before they can move through archive review.';
export const COMMAND_CENTER_NEEDS_ATTENTION_LABEL = 'Needs Attention';
export const COMMAND_CENTER_OPEN_NEEDS_ATTENTION_LABEL = 'Open Needs Attention';

export const COMMAND_CENTER_LATEST_ACTIVITY_TITLE = 'Latest league activity';
export const COMMAND_CENTER_LATEST_ACTIVITY_INTRO_COPY =
  'Recent live detection, replay, and archive movement for your league.';
export const COMMAND_CENTER_LIVE_DETECTION_TITLE = 'Twitch live detection';

export function commandCenterActivationDescription(status: LscAutomationActivationStatus | string): string {
  switch (status) {
    case 'locked_unpaid':
      return 'Activate your LSC subscription before automations can be turned on.';
    case 'blocked':
      return 'Complete setup steps before requesting automation activation.';
    case 'ready_to_activate':
      return 'Your league is eligible to request the default automation bundle.';
    case 'activation_requested':
      return 'Your activation request is saved. Some production gates may still be finishing.';
    case 'pending_operator_enablement':
      return 'Your request is saved. Production automation rollout is still finishing on Scoutlytics infrastructure.';
    case 'entitlement_bridge_disabled':
      return 'Your request is saved. Paid automation runtime will start automatically once Scoutlytics finishes the production rollout.';
    case 'active':
      return 'Your default automation bundle is active for this league.';
    default:
      return 'Review setup and activation readiness below.';
  }
}

export const COMMAND_CENTER_BUNDLE_LANE_LABELS: Record<string, string> = {
  live_detection_eventsub: 'Live detection',
  live_truth_auto_enqueue: 'Live Truth',
  live_home_display: 'Home live display',
  vod_handoff: 'VOD handoff',
  archive_finalization: 'Archive finalization',
  archive_autopublish: 'Archive auto-publish',
};

export function commandCenterBundleLaneStatusLabel(status: string): string {
  switch (status) {
    case 'ready':
      return 'Active';
    case 'warning':
      return 'Needs attention';
    case 'pending_operator_enablement':
      return 'Pending rollout';
    case 'entitlement_bridge_disabled':
      return 'Awaiting rollout';
    case 'locked_unpaid':
      return 'Subscription required';
    case 'not_applicable':
      return 'Off';
    default:
      return 'Pending';
  }
}
